import { useEffect, useId, useRef, type ReactNode } from 'react';
import { usePresence } from '../hooks/usePresence';
import { useI18n } from '../i18n/i18n-context';
import { cx } from '../lib/cx';
import { motionAllowed } from '../lib/motion';
import { Icon } from './Icon';
import './dialog.css';

// A little longer than the --motion-duration of the dialog's way out.
const LEAVE_MS = 200;

interface DialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly wide?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * A modal built on the native <dialog>, which traps focus and closes on Escape. Clicking the
 * backdrop closes it too. Content only renders while open, so forms start fresh each time; once
 * shut, it stays a moment, inert, while the dialog fades away.
 */
export function Dialog({ open, onClose, title, wide, className, children }: DialogProps) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { present, leaving } = usePresence(open, LEAVE_MS);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!present && dialog.open) dialog.close();
  }, [open, present]);

  return (
    <dialog
      ref={ref}
      className={cx('dialog', wide && 'dialog--wide', leaving && 'is-leaving', className)}
      aria-labelledby={titleId}
      // Escape would close the native dialog at once; held, it can fade out like any other close.
      onCancel={(event) => {
        if (!event.cancelable || !motionAllowed()) return;
        event.preventDefault();
        onClose();
      }}
      // Escape closes the native dialog. Closing it here, because `open` changed, isn't news.
      onClose={() => {
        if (open) onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {present && (
        <div className="dialog-panel" inert={leaving}>
          <header className="dialog-header">
            <h2 className="dialog-title" id={titleId}>
              {title}
            </h2>
            <button
              type="button"
              className="button button--ghost icon-button"
              aria-label={t.common.close}
              title={t.common.close}
              onClick={onClose}
            >
              <Icon name="close" />
            </button>
          </header>
          {children}
        </div>
      )}
    </dialog>
  );
}
